import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface ExportModalProps {
  shortId: string;
  /** The clip's own aspect ratio, which is the frame the render uses. Shown, not chosen. */
  aspectRatio: string;
  /** True while the editor holds unsaved changes — the render reads what was saved. */
  dirty?: boolean;
  onClose: () => void;
  segmentStart?: number;
  segmentEnd?: number;
  segmentLabel?: string;
}

const ExportModal: React.FC<ExportModalProps> = ({
  shortId, aspectRatio, dirty, onClose, segmentStart, segmentEnd, segmentLabel,
}) => {
  const isSegmentExport = segmentStart !== undefined && segmentEnd !== undefined;
  const [quality, setQuality] = useState('hd');
  const [codec, setCodec] = useState('h264');
  const [fps, setFps] = useState(30);
  const [exporting, setExporting] = useState(false);
  const [exportId, setExportId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<string>('');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const startExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/shorts/${shortId}/export`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quality, codec, fps,
          ...(isSegmentExport ? { segmentStart, segmentEnd } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Export failed');
      setExportId(data.exportId);
      setStatus('queued');
    } catch (err: any) {
      setError(err.message);
      setExporting(false);
    }
  };

  const pollExport = useCallback(async () => {
    if (!exportId) return;
    try {
      const res = await fetch(`${API_BASE}/api/shorts/${shortId}/export/${exportId}`, {
        credentials: 'include',
      });
      const data = await res.json();
      setStatus(data.status);
      setProgress(data.progress || 0);
      if (data.status === 'completed') {
        setDownloadUrl(data.downloadUrl);
        setExporting(false);
      } else if (data.status === 'failed') {
        setError(data.error || 'Export failed');
        setExporting(false);
      }
    } catch {
      // keep polling
    }
  }, [exportId, shortId]);

  useEffect(() => {
    if (!exportId || !exporting) return;
    const interval = setInterval(pollExport, 2000);
    return () => clearInterval(interval);
  }, [exportId, exporting, pollExport]);

  return (
    <div className="export-modal-backdrop" onClick={onClose}>
      <div className="export-modal" onClick={e => e.stopPropagation()}>
        <div className="export-modal-header">
          <h2>{isSegmentExport ? `Export ${segmentLabel || 'Segment'}` : 'Export Video'}</h2>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>

        {isSegmentExport && (
          <div className="export-segment-badge">
            ⏱ {segmentStart!.toFixed(1)}s – {segmentEnd!.toFixed(1)}s ({(segmentEnd! - segmentStart!).toFixed(1)}s)
          </div>
        )}

        {!exportId ? (
          <div className="export-modal-body">
            <div className="export-field">
              <label>Format</label>
              <span className="export-field-readout" title="The clip's own aspect ratio. Change it in the toolbar to change the exported frame.">
                {aspectRatio} {aspectRatio === '1:1' ? '(Square)' : aspectRatio === '16:9' ? '(Landscape)' : '(Vertical)'}
              </span>
            </div>

            {dirty && (
              <div className="export-dirty-note">
                Unsaved changes are not rendered — save first to include them.
              </div>
            )}
            <div className="export-field">
              <label>Quality</label>
              <select value={quality} onChange={e => setQuality(e.target.value)}>
                <option value="sd">SD (360p)</option>
                <option value="hd">HD (720p)</option>
                <option value="fhd">Full HD (1080p)</option>
                <option value="4k">4K (2160p)</option>
              </select>
            </div>
            <div className="export-field">
              <label>Codec</label>
              <select value={codec} onChange={e => setCodec(e.target.value)}>
                <option value="h264">H.264</option>
                <option value="vp9">VP9</option>
                <option value="av1">AV1</option>
              </select>
            </div>
            <div className="export-field">
              <label>Frame Rate</label>
              <select value={fps} onChange={e => setFps(Number(e.target.value))}>
                <option value={24}>24 fps</option>
                <option value={30}>30 fps</option>
                <option value={60}>60 fps</option>
              </select>
            </div>

            {error && <div className="export-error">{error}</div>}

            <button className="btn-primary export-start-btn" onClick={startExport} disabled={exporting}>
              {exporting ? 'Starting...' : isSegmentExport ? 'Export Segment' : 'Start Export'}
            </button>
          </div>
        ) : (
          <div className="export-modal-body export-progress-body">
            <div className="export-status-label">{status === 'completed' ? 'Export Complete!' : `Status: ${status}`}</div>
            <div className="export-progress-track">
              <div className="export-progress-bar" style={{ width: `${progress}%` }} />
            </div>
            <span className="export-progress-text">{progress}%</span>

            {error && <div className="export-error">{error}</div>}

            {downloadUrl && (
              <a
                href={`${API_BASE}${downloadUrl}`}
                download
                className="btn-primary export-download-btn"
              >
                Download Video
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportModal;
